/**
 * The onboarding gate's one persisted bit (§3.1).
 *
 * The key and the default are **not** declared here. They are entries in the
 * §3.1 preference table in `@ff/store`, and the reason to reach for them rather
 * than retype them is F-NAV-16's versioning rule: the key is versioned — `…v1`
 * — because onboarding replaces the entire shell, so a future revision that
 * needs to be shown again gets a new key rather than a migration. A second copy
 * of that literal in this file is exactly how a user who completed v1 gets
 * re-onboarded by an edit nobody connected to this screen.
 */

import { ONBOARDING_COMPLETED } from "@ff/store";
import { usePreferenceDefinition } from "../../app-state";

/**
 * Re-exported so tests and fixtures can seed the key through the port without
 * importing the whole store table — still one declaration, not two.
 */
export const ONBOARDING_COMPLETED_KEY = ONBOARDING_COMPLETED.key;

export interface OnboardingGate {
  readonly completed: boolean;
  readonly complete: () => void;
}

export function useOnboardingGate(): OnboardingGate {
  const [completed, setCompleted] = usePreferenceDefinition(ONBOARDING_COMPLETED);
  return {
    completed,
    complete: () => {
      setCompleted(true);
    },
  };
}
